// components/ProtectedRoute.js
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import {
  refreshToken,
  isTokenExpired,
  isRefreshCooldownError,
} from "utils/tokenUtils";
import { isNetworkOrServerError } from "utils/networkErrorUtils";

const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isValidating, setIsValidating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Validasi token saat mount - coba refresh, fallback ke accessToken jika valid
  useEffect(() => {
    const validateAuth = async () => {
      const user = localStorage.getItem("user");
      const accessToken = localStorage.getItem("accessToken");
      if (!user) {
        setIsValidating(false);
        setIsAuthenticated(false);
        return;
      }

      try {
        await refreshToken();
        setIsAuthenticated(true);
      } catch (error) {
        // Error jaringan/server atau cooldown: JANGAN redirect ke sign-in - izinkan akses jika ada token valid
        const isNetworkOrCooldown =
          isNetworkOrServerError(error) || isRefreshCooldownError(error);
        if (isNetworkOrCooldown) {
          if (accessToken && !isTokenExpired(accessToken)) {
            setIsAuthenticated(true);
          } else {
            // Server down + token expired: tetap izinkan agar tidak lockout (API akan gagal, user bisa cek nanti)
            setIsAuthenticated(true);
          }
          setIsValidating(false);
          return;
        }

        // Refresh gagal karena auth (401/cookie) tetapi accessToken masih valid
        const isNoRefreshToken =
          error.response?.data?.message?.toLowerCase?.().includes("refresh token") ||
          error.response?.status === 401;
        if (isNoRefreshToken && accessToken && !isTokenExpired(accessToken)) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
          navigate("/authentication/sign-in/cover", {
            state: { from: location.pathname },
            replace: true,
          });
        }
      } finally {
        setIsValidating(false);
      }
    };

    validateAuth();
  }, [navigate, location.pathname]);

  if (isValidating) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/authentication/sign-in/cover"
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  return children;
};

export default ProtectedRoute;
